import React, { useEffect, useState, useMemo } from 'react';
import { Button, Card, message, Typography, Image, Tooltip, Skeleton } from 'antd';
import { HomeOutlined, EditOutlined, FilePdfOutlined, MailOutlined } from '@ant-design/icons';

import CustomerResolutionForm from './CustomerResolutionForm'; // This form can now handle both create and update modes
import CustomerResolutionPreview from './CustomerResolutionPreview';
import mohLogo from '../../../assets/images/Mohawk_Industries_logo.png';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import { saveFsInspectionReport, getFsInspectionReport, getFsInspectionRequest, generatePdfInspectionReport } from '../../../services/FsReportApis';
import useAutosave from '../../../hooks/useAutosave';
import AutosaveIndicator from '../../../components/AutosaveIndicator';
import * as AutosaveService from '../../../services/autosaveService';
import * as quickReportService from '../../../services/createQuickReport';
 
const CustomerResolutionReportContainer = () => {
    const queryParams = useMemo(() => new URLSearchParams(window.location.search), []);
    const reportId = queryParams.get('report_id');
    const requestId = queryParams.get('req_id');

    const [currentView, setCurrentView] = useState('form'); // 'form', 'preview'
    const [reportData, setReportData] = useState({ report_id: reportId, req_id: requestId }); // Initialize with IDs from URL
    const [formData, setFormData] = useState({});
    const [attachments, setAttachments] = useState([]);
    const [videoAttachments, setVideoAttachments] = useState([]);
    const { Title, Text } = Typography;
    const [loading, setLoading] = useState(false);
    const [pdfLoading, setPdfLoading] = useState(false);
    const dispatch = useDispatch();
    const [skipNextFetch, setSkipNextFetch] = useState(false);  
    const navigate = useNavigate();
    const userDetails = useSelector((state) => state.User.userDetails);
    const userRole = userDetails?.user_role;
    const [autosaveEnabled, setAutosaveEnabled] = useState(false);

    const transformValuesForAutosave = (values) => {
        const transformedValues = { ...values };
        transformedValues.reportId = reportId;
        transformedValues.reqId = requestId;
        return transformedValues;
    };

    const { status, error } = useAutosave(
        formData,
        (data) => quickReportService.updateQuickReport(transformValuesForAutosave(data)),
        { enabled: autosaveEnabled }
      );

    const handleFormSubmit = (formData) => {
        
        setLoading(true);
        const updatedFormData = {
            ...formData,
            ...(reportId && { reportId: reportId }),
            ...(requestId && { reqId: requestId }),
            attachments,
            video_attachments: videoAttachments
        };
        //console.warn("handleFormSubmit",updatedFormData);
        //return true;
        dispatch(saveFsInspectionReport(updatedFormData))
            .then((response) => {
                setLoading(false);
                setCurrentView('preview');
                if (response.reportId) {
                    const newReportData = {
                        ...response.reportData,
                        report_id: response.reportId,
                        req_id: response.reportData.req_id || requestId
                    };
                    setReportData(newReportData);
                    console.log("Report data set successfully", newReportData);
                    // Set the flag to skip the next fetch
                    setSkipNextFetch(true);
                    navigate(`/field-service-resolution-form/?report_id=${response.reportId}`)
                }
                message.success('Report submitted successfully!');
                console.log("Report saved successfully", response);
            })
            .catch((err) => {
                setLoading(false);
                console.error("Failed to save report", err)
            });

    };

    const handleGeneratePdf = (id) => {
        const finalId = id || reportId;
        if (!finalId) {
            message.error("Report ID is missing. Please ensure the report ID is available.");
            return;
        }

        setPdfLoading(true);
        dispatch(generatePdfInspectionReport(finalId))
            .then((res) => {
                setPdfLoading(false);
                window.open(res.pdf_url);
                // Refresh data to get the new report_pdf URL
                fetchData(finalId);
            })
            .catch((error) => {
                setPdfLoading(false);
                console.error("Failed to generate PDF report", error);
                message.error("Failed to generate PDF report. Please try again.");
            });
    };
    useEffect(() => {

        if (skipNextFetch) {
            setSkipNextFetch(false);
            return;
        }
        const newQueryParams = new URLSearchParams(window.location.search);
        const newReportId = newQueryParams.get('report_id');
        const newRequestId = newQueryParams.get('req_id');
        
        if (newReportId) {
            fetchData(newReportId);
        } else if (newRequestId && !newReportId) {
            fetchData();
        }

        AutosaveService.getAutosaveSetting()
        .then(res => {
          if (res.data && res.data.enabled !== undefined) {
            const isBackOfficeUser = userRole === 'um_claim-analyst' || userRole === 'um_fs_manager' || userRole === 'um_manufacturer';
            setAutosaveEnabled(res.data.enabled && !isBackOfficeUser);
          }
        })
        .catch(err => {
          console.error("Error fetching autosave setting", err);
        });

    }, [window.location.search, userRole]);  // Just use this single useEffect
    const convertToArray = (images) => {
        if (Array.isArray(images)) {
            return images;
        } else if (images && typeof images === 'object') {
            // If it's an object with numbered keys (like your second example)
            if (Object.keys(images).some(key => !isNaN(parseInt(key)))) {
                return Object.values(images);
            }
            // If it's a single object
            return [images];
        }
        return [];
    };
    const fetchData = async (id = null) => {
        try {
            setLoading(true);
            let data = null;
            if (id) {
                console.log("Inside fetchData with id:", id); // Add logging
                data = await dispatch(getFsInspectionReport(id));
                console.log("Received data:", data); // Add logging
            } else if (requestId && !id) {
                console.log("Inside fetchData with requestId:", requestId); // Add logging
                data = await dispatch(getFsInspectionRequest(requestId));
                console.log("Received data from request:", data); // Add logging
            }
    
            if (data && Object.keys(data).length > 0) {
                console.log("Received data:", data); // Add logging
                const mergedData = { 
                    report_id: reportId || data.report_id, 
                    req_id: requestId || data.req_id || data.reqId, 
                    ...data 
                };
                setReportData(mergedData);
                setFormData(mergedData);
                // Only switch to preview if we have valid data and an ID
                if (id) setCurrentView('preview');
            } else {
                message.warning("Retrieved data is empty or incomplete");
                // Stay in form view if data is missing
                setCurrentView('form');
            }
    
            setLoading(false);
        } catch (err) {
            console.error("Failed to fetch report or request", err);
            message.error(`Failed to load data: ${err.message || "Unknown error"}`);
            setLoading(false);
        }
    };
       
    const ReportHeader = () => (
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>

            <Image
                src={mohLogo}
                alt="Mohawk Logo"
                preview={false}
                className='mohawak-logo'
                style={{ maxHeight: '100px', marginBottom: '16px' }}
            />

            <Title level={2}>Inspection Report</Title>

            {loading ? (
                <Skeleton.Input active size="default" style={{ width: '40%', marginBottom: '8px' }} />
            ) : (
                <Text>Address: 160 S. Industrial Blvd. Calhoun, GA 30701</Text>
            )}

           
        </div>
    );
    const navigateToHome = () => {
        navigate('/'); // React router navigation instead of full reload
    };

    return (
        <div className='main-content-section' style={styles.container}>
            <div className='container py-0  my-5'>
                <div className='row'>
                    <Card className='tablet-padding-0' style={styles.card}>
                        <Button
                            icon={<HomeOutlined />}
                            onClick={navigateToHome}
                            className='home-mohawak-btn'
                            style={{ ...styles.button, marginBottom: '16px' }}
                        >
                            Home
                        </Button>
                        <ReportHeader />
                        {autosaveEnabled && <AutosaveIndicator status={status} error={error} />}
                        {currentView === 'form' && (
                            <CustomerResolutionForm
                                onFinish={handleFormSubmit}
                                loading={loading}
                                setLoading={setLoading}
                                initialData={reportData} // Pass reportData for update mode
                                onValuesChange={(_, values) => setFormData(values)}
                                setAttachments={setAttachments}
                                setVideoAttachments={setVideoAttachments}
                            />
                        )}
                        {currentView === 'preview' && (
                            <>
                                <CustomerResolutionPreview formData={reportData} loading={loading} />
                                <div className="circle-button-div">
                                    {(userRole === 'um_fs_inspector' || userRole === 'um_fs_manager') && (
                                        <Tooltip title="Edit Report" placement="top">
                                            <Button
                                                type="primary"
                                                shape="circle"
                                                icon={<EditOutlined className="svg-icon" />}
                                                onClick={() => setCurrentView('form')}
                                                className="circle-custom-button"
                                                style={{ marginRight: '4px' }}
                                            />
                                        </Tooltip>
                                    )}
                                    {(reportId || reportData.report_id) && (
                                        <Tooltip title="Generate PDF" placement="top">
                                            <Button
                                                type="primary"
                                                shape="circle"
                                                loading={pdfLoading}
                                                icon={<FilePdfOutlined className="svg-icon" />}
                                                onClick={() => handleGeneratePdf(reportId || reportData.report_id)} // Pass ID explicitly
                                                className="circle-custom-button"
                                                style={{ marginRight: '4px' }}
                                            />
                                        </Tooltip>
                                    )}
                                    {reportData.req_id && reportData.report_generated === "YES" && reportData.report_pdf && (
                                        <Tooltip title="Send Email" placement="top">
                                            <Button
                                                type="primary"
                                                shape="circle"
                                                icon={<MailOutlined className="svg-icon" />}
                                                onClick={() =>
                                                    navigate('/send-email/?req_id=' + reportData.req_id)
                                                }
                                                className="circle-custom-button"
                                            />
                                        </Tooltip>
                                    )}
                                </div>
                            </>
                        )}

                    </Card>
                </div>
            </div>
        </div>

    );
};

const styles = {
    container: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#f0f2f5',
    },
    card: {
        // width: '60%',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        borderRadius: '12px',
        backgroundColor: '#fff',
    },
    button: {
        marginTop: '15px',
        borderRadius: '6px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    },
};

export default CustomerResolutionReportContainer;
